import { ref, computed, toValue } from 'vue'
import axios from '@/services/axios.js'

export const UNITED_KINGDOM = { countryCode: 'GB', name: 'United Kingdom' }

export function useAddress(country = UNITED_KINGDOM) {
  const suggestions = ref([])
  const selectedAddress = ref({})
  const isOpen = ref(false)
  const searchTerm = ref('')
  const isLoading = ref(false)
  const error = ref(null)

  const API_KEY = import.meta.env.VITE_ADDRESS_AUTOCOMPLETE_API_KEY
  const BASE_URL = 'https://api.addressy.com/Capture/Interactive'
  const CONTAINER_TYPE = 'Container'

  const hasSuggestions = computed(() => suggestions.value.length > 0)
  const dropdownIsOpen = computed(() => isOpen.value)
  const currentSearchTerm = computed(() => searchTerm.value)

  const fetchSuggestions = async (term, containerId) => {
    searchTerm.value = term
    error.value = null

    if (!term && !containerId) {
      suggestions.value = []
      isOpen.value = false
      return
    }

    isLoading.value = true

    try {
      const response = await axios.get(`${BASE_URL}/Find/v1.10/json6.ws`, {
        params: {
          Key: API_KEY,
          Text: term,
          Container: containerId,
          Countries: toValue(country).countryCode,
        },
      })

      let results = response.data.Items || []

      suggestions.value = results.map((result) => ({
        id: result.Id,
        text: result.Text,
        type: result.Type,
        description: result.Description,
        highlight: result.Highlight,
        address: result.Text, // Adding this to match the expected structure
      }))

      isOpen.value = true
    } catch (err) {
      console.error('Error fetching address suggestions:', err)
      error.value = 'Failed to fetch address suggestions'
      suggestions.value = []
    } finally {
      isLoading.value = false
    }
  }

  const selectAddress = async (suggestion) => {
    searchTerm.value = suggestion.address
    isOpen.value = false
    isLoading.value = true
    error.value = null

    if (suggestion.type == CONTAINER_TYPE) {
      fetchSuggestions(null, suggestion.id)
      return
    }

    try {
      const response = await axios.get(`${BASE_URL}/Retrieve/v1.20/json6.ws`, {
        params: {
          Key: API_KEY,
          Id: suggestion.id,
          Field1Format: '{UPRN}',
        },
      })

      const result = response.data?.Items[0]
      // Map the API response to our standardized address format
      selectedAddress.value = {
        // First line is always the primary address line
        address_line_1: result.Line1 ?? '',

        // If there's a building name and it's different from Line2, use it as line 2, otherwise use Line2 from API
        address_line_2:
          result.BuildingName && result.BuildingName !== result.Line2
            ? result.BuildingName
            : (result.Line2 ?? ''),

        // If building name exists and is different from Line2, Line2 becomes line 3, otherwise use Line3 from API
        address_line_3:
          result.BuildingName && result.BuildingName !== result.Line2
            ? (result.Line2 ?? '')
            : (result.Line3 ?? ''),

        // Prefer District over City for town field (District isnt populated for City Centre addresses)
        town: result.District ? result.District : result.City,

        // Province/County information
        county: result.ProvinceName ?? '',

        // Postal code
        postcode: result.PostalCode ?? '',

        country: toValue(country).name,

        crm_uprn:
          toValue(country).countryCode === UNITED_KINGDOM.countryCode
            ? (result.Field1 ?? '')
            : null,
      }

      return selectedAddress.value
    } catch (err) {
      console.error('Error fetching address details:', err)
      error.value = 'Failed to fetch address details'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const toggleDropdown = () => {
    isOpen.value = !isOpen.value
  }

  const closeDropdown = () => {
    isOpen.value = false
  }

  const setSearchTerm = (term) => {
    searchTerm.value = term
  }

  return {
    suggestions,
    selectedAddress,
    hasSuggestions,
    dropdownIsOpen,
    currentSearchTerm,
    isLoading,
    error,
    fetchSuggestions,
    selectAddress,
    toggleDropdown,
    closeDropdown,
    setSearchTerm,
  }
}
